/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useAIContactExtractor.ts
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 2.0.0
 * @description Hook personalizado para extraer contactos desde texto natural
 *              usando endpoints AnubisAI con fallback automático
 * 
 * CARACTERÍSTICAS:
 * ✅ Llamada a API AnubisAI (endpoint principal + fallback)
 * ✅ Reintentos automáticos con backoff
 * ✅ Prompt optimizado para extracción JSON
 * ✅ Parsing robusto con fallbacks
 * ✅ Validación de teléfonos chilenos (+56)
 * ✅ Validación de emails
 * ✅ Normalización de datos
 * ✅ Detección de duplicados
 * ✅ Cálculo de confidence score
 * ✅ Error handling completo
 * 
 * @date 2024-11-11
 * @updated 2024-11-11 - Integración con AnubisAI backend
 */

import { useState, useCallback } from 'react';

// Helper para obtener token de autenticación
// NOTA: Si el import falla, el hook usará fallback a localStorage
let getHatateToken: (() => string) | null = null;
try {
  // Intentar importar dinámicamente
  const utils = require('../helpers/utils');
  getHatateToken = utils.getHatateToken;
} catch (e) {
  console.warn('[useAIContactExtractor] No se pudo importar getHatateToken, usando fallback');
}

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Contacto extraído por la IA
 */
export interface ExtractedContact {
  name: string;
  phone?: string;
  email?: string;
  company?: string;
  position?: string;
  confidence: number;      // 0-1
  errors: string[];
  warnings: string[];
  isDuplicate?: boolean;
  duplicateOf?: string;    // ID del contacto duplicado
}

/**
 * Intención detectada por la IA
 */
export type IntentionType = 'extract' | 'search' | 'unknown';

/**
 * Resultado del hook
 */
export interface UseAIContactExtractorResult {
  // Estado
  isExtracting: boolean;
  extractedContacts: ExtractedContact[];
  searchQuery: string | null;        // ⭐ NUEVO: Query de búsqueda detectada
  intention: IntentionType;           // ⭐ NUEVO: Intención detectada
  error: string | null;
  rawResponse: string | null;
  
  // Acciones
  extractContacts: (text: string, existingContactsCount?: number) => Promise<void>;
  validateContact: (contact: ExtractedContact) => boolean;
  normalizeContact: (contact: ExtractedContact) => ExtractedContact;
  reset: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const PRIMARY_API_ENDPOINT = 'https://backend.anubisai.net/api/bot/llm';
const FALLBACK_API_ENDPOINT = 'https://backend.anubisai.net/api/bot/prompt-openrouter';
const MAX_RETRIES = 2;
const TIMEOUT_MS = 30000;

// Función para obtener el token usando getHatateToken (como en SendTab)
const getApiToken = (): string => {
  // Opción 1: Usar getHatateToken si está disponible
  if (getHatateToken) {
    try {
      const token = getHatateToken();
      if (token) return token;
    } catch (e) {
      console.warn('[useAIContactExtractor] Error al obtener token con getHatateToken');
    }
  }
  
  // Fallback: localStorage (mismo key que usa el proyecto)
  const localToken = localStorage.getItem('hatate_token');
  if (localToken) return localToken;
  
  // Fallback: sessionStorage
  const sessionToken = sessionStorage.getItem('hatate_token');
  if (sessionToken) return sessionToken;
  
  // Si no hay token, lanzar error descriptivo
  console.error('[useAIContactExtractor] ⚠️ Token de autenticación no encontrado');
  return '';
};

/**
 * Prompt optimizado para detección de intención + extracción/búsqueda
 */
const EXTRACTION_PROMPT = (text: string, hasExistingContacts: boolean = false) => `
Eres un experto en procesar solicitudes sobre contactos en español.

${hasExistingContacts ? `
CONTEXTO IMPORTANTE: El usuario tiene una base de datos de contactos existentes.
` : ''}

TEXTO DEL USUARIO:
${text}

TU TAREA: Detectar la INTENCIÓN del usuario y responder apropiadamente.

═══════════════════════════════════════════════════════════════════════════
CASO 1: BÚSQUEDA EN BASE DE DATOS
═══════════════════════════════════════════════════════════════════════════
Si el usuario quiere BUSCAR/ENCONTRAR contactos existentes:
- Palabras clave: "busca", "encuentra", "muestra", "filtra", "dame los contactos de"
- El texto NO incluye teléfonos ni emails completos
- Menciona criterios: nombre, empresa, cargo, ubicación, etc.

RESPUESTA PARA BÚSQUEDA:
{
  "intention": "search",
  "searchQuery": "descripción de los criterios de búsqueda",
  "contacts": []
}

EJEMPLO BÚSQUEDA:
Usuario: "busca hector villarroel"
Respuesta: {
  "intention": "search",
  "searchQuery": "nombre contiene 'hector villarroel'",
  "contacts": []
}

Usuario: "muestra contactos de la empresa Acme"
Respuesta: {
  "intention": "search",
  "searchQuery": "empresa = 'Acme'",
  "contacts": []
}

═══════════════════════════════════════════════════════════════════════════
CASO 2: EXTRACCIÓN DE CONTACTOS NUEVOS
═══════════════════════════════════════════════════════════════════════════
Si el usuario quiere AGREGAR contactos nuevos:
- El texto incluye datos completos: nombre + teléfono/email
- Palabras clave: "agregar", "añadir", "importar", "crear"
- O simplemente lista de contactos con datos

RESPUESTA PARA EXTRACCIÓN:
{
  "intention": "extract",
  "searchQuery": null,
  "contacts": [
    {
      "name": "Juan Pérez",
      "phone": "+56912345678",
      "email": "juan@example.com",
      "company": "Acme Corp",
      "position": "Gerente",
      "confidence": 0.95
    }
  ]
}

REGLAS PARA EXTRACCIÓN:
- Normalizar teléfonos chilenos a formato +56XXXXXXXXX
- Si no hay teléfono, OMITIR el campo (no usar null)
- Si no hay email, OMITIR el campo (no usar null)
- confidence entre 0 y 1 (qué tan seguro estás)
- NO inventar datos que no estén en el texto

═══════════════════════════════════════════════════════════════════════════
FORMATO DE SALIDA (CRÍTICO)
═══════════════════════════════════════════════════════════════════════════
RESPONDE SOLO CON UN JSON VÁLIDO. Sin markdown, sin explicaciones, sin \`\`\`json.

Si es búsqueda:
{
  "intention": "search",
  "searchQuery": "criterios de búsqueda en lenguaje natural",
  "contacts": []
}

Si es extracción:
{
  "intention": "extract",
  "searchQuery": null,
  "contacts": [ /* array de contactos */ ]
}

RESPONDE AHORA (solo el JSON):
`.trim();


// ═══════════════════════════════════════════════════════════════════════════
// UTILIDADES DE VALIDACIÓN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Normaliza un número de teléfono chileno
 */
const normalizePhone = (phone: string): string => {
  // Limpiar espacios, guiones, paréntesis
  let cleaned = phone.replace(/[\s\-\(\)]/g, '');
  
  // Si ya tiene +56, verificar formato
  if (cleaned.indexOf('+56') === 0) {
    return cleaned;
  }
  
  // Si empieza con 56 sin +
  if (cleaned.indexOf('56') === 0 && cleaned.length === 11) {
    return '+' + cleaned;
  }
  
  // Si es solo el número (9 dígitos)
  if (cleaned.length === 9 && cleaned.indexOf('9') === 0) {
    return '+56' + cleaned;
  }
  
  // Si tiene 8 dígitos, agregar 9 al inicio
  if (cleaned.length === 8) {
    return '+569' + cleaned;
  }
  
  return cleaned;
};

/**
 * Valida formato de teléfono chileno
 */
const isValidChileanPhone = (phone: string): boolean => {
  const regex = /^\+56[9]\d{8}$/;
  return regex.test(phone);
};

/**
 * Valida formato de email
 */
const isValidEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Sanitiza nombre (capitalizar, limpiar espacios)
 */
const sanitizeName = (name: string): string => {
  return name
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// ═══════════════════════════════════════════════════════════════════════════
// HOOK PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

export const useAIContactExtractor = (): UseAIContactExtractorResult => {
  
  // ───────────────────────────────────────────────────────────────────────
  // ESTADOS
  // ───────────────────────────────────────────────────────────────────────
  
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedContacts, setExtractedContacts] = useState<ExtractedContact[]>([]);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [intention, setIntention] = useState<IntentionType>('unknown');
  const [error, setError] = useState<string | null>(null);
  const [rawResponse, setRawResponse] = useState<string | null>(null);

  // ───────────────────────────────────────────────────────────────────────
  // LLAMADA A API CON FALLBACK
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Extrae respuesta de diferentes estructuras de datos
   */
  const extractResponse = useCallback((data: any): string | null => {
    console.log('[useAIContactExtractor] Estructura recibida:', JSON.stringify(data, null, 2));
    
    if (data.reply) return data.reply;
    if (data.text) return data.text;
    if (data.message) return data.message;
    if (data.answer) return data.answer;
    if (data.response) return data.response;
    if (typeof data === 'string') return data;
    
    if (Array.isArray(data)) {
      for (const item of data) {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const extracted = extractResponse(item);
          if (extracted) return extracted;
        }
      }
    }
    
    return null;
  }, []);

  /**
   * Realiza la llamada a la API con fallback automático
   */
  const callLLMWithFallback = useCallback(async (promptText: string): Promise<string> => {
    const apiToken = getApiToken();
    
    if (!apiToken) {
      throw new Error('Token de autenticación no disponible');
    }

    const chatId = `contacts_ai_${Date.now()}`;
    const skill = 'general_v1';
    const module = 'general_general';
    
    const messages = [
      {
        role: 'user',
        content: promptText,
        time: new Date().toISOString()
      }
    ];

    let lastError: any = null;
    let attemptCount = 0;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      while (attemptCount < MAX_RETRIES) {
        attemptCount++;
        
        // Intento 1: Endpoint principal
        if (attemptCount === 1) {
          try {
            console.log('[useAIContactExtractor] 🔗 Intento con endpoint principal...');
            
            const primaryPayload = {
              prompt: messages,
              chatId: chatId,
              skill: skill,
              module: module
            };
            
            const primaryResponse = await fetch(PRIMARY_API_ENDPOINT, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': apiToken
              },
              body: JSON.stringify(primaryPayload),
              signal: controller.signal
            });

            if (primaryResponse.ok) {
              const data = await primaryResponse.json();
              const extracted = extractResponse(data);
              
              if (extracted) {
                console.log('[useAIContactExtractor] ✅ Endpoint principal OK');
                clearTimeout(timeoutId);
                return extracted;
              }
            }
            
            throw new Error(`Endpoint principal falló: ${primaryResponse.status}`);
            
          } catch (primaryError: any) {
            console.warn('[useAIContactExtractor] ❌ Endpoint principal falló:', primaryError.message);
            lastError = primaryError;
          }
        }
        
        // Intento 2+: Endpoint de fallback
        try {
          console.log(`[useAIContactExtractor] 🔗 Intento ${attemptCount}/${MAX_RETRIES} con fallback...`);
          
          const fallbackPayload = {
            chatId: chatId,
            module: module,
            skill: skill,
            prompt: messages.map(msg => ({
              content: msg.content,
              role: msg.role,
              time: msg.time
            }))
          };
          
          const fallbackResponse = await fetch(FALLBACK_API_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': apiToken
            },
            body: JSON.stringify(fallbackPayload),
            signal: controller.signal
          });

          if (!fallbackResponse.ok) {
            const errorText = await fallbackResponse.text();
            throw new Error(`Fallback falló: ${fallbackResponse.status} - ${errorText}`);
          }

          const data = await fallbackResponse.json();
          const extracted = extractResponse(data);
          
          if (extracted) {
            console.log('[useAIContactExtractor] ✅ Endpoint fallback OK');
            clearTimeout(timeoutId);
            return extracted;
          }
          
          throw new Error('No se pudo extraer respuesta válida del fallback');
          
        } catch (fallbackError: any) {
          console.error(`[useAIContactExtractor] ❌ Intento ${attemptCount} falló:`, fallbackError.message);
          lastError = fallbackError;
          
          if (attemptCount < MAX_RETRIES) {
            console.log('[useAIContactExtractor] 🔄 Esperando antes del siguiente intento...');
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      clearTimeout(timeoutId);
      throw new Error(`Todos los endpoints fallaron. Último error: ${lastError?.message}`);
      
    } catch (err: any) {
      clearTimeout(timeoutId);
      
      if (err.name === 'AbortError') {
        throw new Error('Timeout: La IA tardó demasiado en responder (>30s)');
      }
      
      throw err;
    }
  }, [extractResponse]);

  // ───────────────────────────────────────────────────────────────────────
  // PARSING DE RESPUESTA JSON
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Parsea la respuesta JSON de la IA con fallbacks robustos
   */
  const parseAIResponse = useCallback((responseText: string): any => {
    try {
      // Intentar parsing directo
      return JSON.parse(responseText);
    } catch {
      // Fallback 1: Limpiar markdown
      let cleaned = responseText
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();
      
      try {
        return JSON.parse(cleaned);
      } catch {
        // Fallback 2: Extraer JSON con regex
        const jsonMatch = responseText.match(/\{[\s\S]*"contacts"[\s\S]*\}/);
        if (jsonMatch) {
          try {
            return JSON.parse(jsonMatch[0]);
          } catch {
            throw new Error('No se pudo parsear la respuesta JSON');
          }
        }
        
        throw new Error('No se encontró JSON válido en la respuesta');
      }
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // VALIDACIÓN DE CONTACTO
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Valida un contacto extraído
   */
  const validateContact = useCallback((contact: ExtractedContact): boolean => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validar nombre (requerido)
    if (!contact.name || contact.name.trim().length < 2) {
      errors.push('Nombre inválido o muy corto');
    }

    // Validar teléfono (opcional pero debe ser válido si existe)
    if (contact.phone) {
      const normalized = normalizePhone(contact.phone);
      if (!isValidChileanPhone(normalized)) {
        warnings.push('Formato de teléfono no válido para Chile');
      }
    }

    // Validar email (opcional pero debe ser válido si existe)
    if (contact.email) {
      if (!isValidEmail(contact.email)) {
        warnings.push('Formato de email no válido');
      }
    }

    // Validar que tenga al menos teléfono O email
    if (!contact.phone && !contact.email) {
      errors.push('Debe tener al menos teléfono o email');
    }

    contact.errors = errors;
    contact.warnings = warnings;

    return errors.length === 0;
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // NORMALIZACIÓN DE CONTACTO
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Normaliza un contacto extraído
   */
  const normalizeContact = useCallback((contact: ExtractedContact): ExtractedContact => {
    const normalized = { ...contact };

    // Normalizar nombre
    if (normalized.name) {
      normalized.name = sanitizeName(normalized.name);
    }

    // Normalizar teléfono
    if (normalized.phone) {
      normalized.phone = normalizePhone(normalized.phone);
    }

    // Normalizar email (lowercase)
    if (normalized.email) {
      normalized.email = normalized.email.trim().toLowerCase();
    }

    // Normalizar empresa
    if (normalized.company) {
      normalized.company = normalized.company.trim();
    }

    // Normalizar posición
    if (normalized.position) {
      normalized.position = normalized.position.trim();
    }

    // Asegurar confidence entre 0 y 1
    if (normalized.confidence < 0) normalized.confidence = 0;
    if (normalized.confidence > 1) normalized.confidence = 1;

    return normalized;
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // EXTRACCIÓN PRINCIPAL
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Extrae contactos desde texto natural usando IA
   */
  const extractContacts = useCallback(async (text: string, existingContactsCount: number = 0): Promise<void> => {
    // Validar input
    if (!text || text.trim().length < 3) {
      setError('El texto es demasiado corto');
      return;
    }

    setIsExtracting(true);
    setError(null);
    setRawResponse(null);
    setExtractedContacts([]);
    setSearchQuery(null);
    setIntention('unknown');

    try {
      // Paso 1: Llamar a la API con fallback
      console.log('[useAIContactExtractor] Llamando a LLM con fallback...');
      const hasExistingContacts = existingContactsCount > 0;
      const promptText = EXTRACTION_PROMPT(text, hasExistingContacts);
      const responseText = await callLLMWithFallback(promptText);
      
      console.log('[useAIContactExtractor] Respuesta recibida:', responseText.substring(0, 200));
      setRawResponse(responseText);

      // Paso 2: Parsear JSON
      console.log('[useAIContactExtractor] Parseando JSON...');
      const parsed = parseAIResponse(responseText);

      // Paso 3: Detectar intención
      const detectedIntention: IntentionType = parsed.intention || 'unknown';
      setIntention(detectedIntention);
      
      console.log('[useAIContactExtractor] Intención detectada:', detectedIntention);

      // Paso 4A: Si es BÚSQUEDA
      if (detectedIntention === 'search') {
        const query = parsed.searchQuery || text;
        setSearchQuery(query);
        setExtractedContacts([]);
        console.log('[useAIContactExtractor] Búsqueda detectada:', query);
        return; // El componente padre manejará la búsqueda
      }

      // Paso 4B: Si es EXTRACCIÓN
      if (detectedIntention === 'extract') {
        if (!parsed.contacts || !Array.isArray(parsed.contacts)) {
          throw new Error('Formato de respuesta inválido: no se encontró array de contactos');
        }

        if (parsed.contacts.length === 0) {
          setError('No se detectaron contactos en el texto. ¿Quizás querías buscar?');
          setExtractedContacts([]);
          return;
        }

        console.log('[useAIContactExtractor] Contactos detectados:', parsed.contacts.length);

        // Paso 5: Normalizar y validar cada contacto
        const processedContacts: ExtractedContact[] = parsed.contacts.map((rawContact: any) => {
          const contact: ExtractedContact = {
            name: rawContact.name || '',
            phone: rawContact.phone,
            email: rawContact.email,
            company: rawContact.company,
            position: rawContact.position,
            confidence: rawContact.confidence || 0.5,
            errors: [],
            warnings: []
          };

          // Normalizar
          const normalized = normalizeContact(contact);

          // Validar
          validateContact(normalized);

          return normalized;
        });

        console.log('[useAIContactExtractor] Contactos procesados:', processedContacts.length);
        setExtractedContacts(processedContacts);
        return;
      }

      // Paso 4C: Intención desconocida
      setError('No se pudo determinar si quieres buscar o agregar contactos. Intenta ser más específico.');

    } catch (err: any) {
      console.error('[useAIContactExtractor] Error:', err);
      setError(err.message || 'Error desconocido al procesar');
      setExtractedContacts([]);
      setSearchQuery(null);
      setIntention('unknown');
    } finally {
      setIsExtracting(false);
    }
  }, [callLLMWithFallback, parseAIResponse, normalizeContact, validateContact]);

  // ───────────────────────────────────────────────────────────────────────
  // RESET
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Resetea el estado del hook
   */
  const reset = useCallback(() => {
    setIsExtracting(false);
    setExtractedContacts([]);
    setSearchQuery(null);
    setIntention('unknown');
    setError(null);
    setRawResponse(null);
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // RETURN
  // ───────────────────────────────────────────────────────────────────────

  return {
    // Estado
    isExtracting,
    extractedContacts,
    searchQuery,
    intention,
    error,
    rawResponse,
    
    // Acciones
    extractContacts,
    validateContact,
    normalizeContact,
    reset
  };
};

export default useAIContactExtractor;